// #region imports

import React from 'react';
import { format } from 'date-fns';

// Material-UI
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import {
  DataGrid,
  GridCellParams,
  GridColDef
} from '@mui/x-data-grid';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  HTTPHeaders
} from '../../interfaces';

import {
  SignupCancelled
} from '../../models';

import {
  API_URL,
  NULL_ID
} from '../../global';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';

// UTC to local date formatting
import { useDates } from '../../hooks/UseDates';

// Responsive hooks
import { useResponsive } from '../../hooks/UseResponsive';

// #endregion

// #region Interfaces

interface CancelledSignedupsProps {
  closeDrawer: () => void;
  signupID: number;
}

// #endregion

const CancelledSignup: React.FC<CancelledSignedupsProps> = (props) => {

  const { getAccessToken } = useAuth0User();
  const { dateStringToLocalDateFormatted } = useDates();
  const { isMobile } = useResponsive();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [signup, setSignup] = React.useState<SignupCancelled>(new SignupCancelled());

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      padding: theme.spacing(1, 1, 1, 1)
    },
    divider: {
      margin: theme.spacing(1, 0, 1, 0)
    },
    sectionHeading: {
      margin: theme.spacing(1, 0, 1, 0)
    },
  };

  // #endregion

  // #region Methods

  /*
* Grid column definitions
*/
  const guestColumns = (): Array<GridColDef> => {
    return [
      {
        field: 'lastName',
        headerName: 'Name',
        width: 150,
        type: 'string',
        renderCell: (params: GridCellParams) => { return renderName(params); }
      }, {
        field: 'arrivalDate',
        headerName: 'Dates',
        width: isMobile ? 100 : 125,
        type: 'string',
        renderCell: (params: GridCellParams) => { return renderDates(params); }
      }, {
        field: 'guestType',
        headerName: 'Type',
        hide: isMobile,
        width: 150,
        type: 'string',
      }];

  }

  /*
* Grid column definitions
*/
  const memberColumns = (): Array<GridColDef> => {
    return [
      {
        field: 'lastName',
        headerName: 'Name',
        width: isMobile ? 125 : 150,
        type: 'string',
        renderCell: (params: GridCellParams) => { return renderName(params); }
      }, {
        field: 'arrivalDate',
        headerName: 'Dates',
        width: isMobile ? 100 : 125,
        type: 'string',
        renderCell: (params: GridCellParams) => { return renderDates(params); }
      }, {
        field: 'memberStatus',
        headerName: 'Type',
        hide: isMobile,
        width: 150,
        type: 'string',
      }];

  }

  /*
   * Fetch signup data
   */
  const fetchSignup = async () => {

    setIsLoading(true);

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const url = new URL(`${API_URL}/v1/signups/cancelled/${props.signupID}`);

    await fetch(url.toString(),
      {
        method: 'GET',
        cache: 'no-cache',
        headers: headers,
        mode: 'cors'
      })
      .then((response) => {
        if (!response.ok) {
          setIsLoading(false);
          throw new Error('Cancelled signups not retrieved');
        }
        return response.json()
      })
      .then((signup: SignupCancelled) => {
        setSignup(signup);
        setIsLoading(false);
      })
      .catch(err => {
        setIsLoading(false);
        throw new Error('Cancelled signups not retrieved');
      });

  }

  /*
  * Render dates
  */
  const renderDates = (params: GridCellParams) => {
    return `${format(new Date(params.row.arrivalDate), 'MM/dd')}-${format(new Date(params.row.departureDate), 'MM/dd')}`
  }

  /*
* Render member or guest name
*/
  const renderName = (params: GridCellParams) => {
    return `${params.row.lastName}, ${params.row.firstName}`
  }

  // #endregion

  // #region Event handlers

  React.useEffect(() => {
    if ((props.signupID !== NULL_ID) && (signup.signupID !== props.signupID)) {
      fetchSignup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.signupID]);

  // #endregion

  return (
    <Box sx={styles.root}>
      <Grid container alignItems="center">
        {isMobile &&
          <Grid>
            <IconButton onClick={props.closeDrawer} size="large">
              <CloseIcon />
            </IconButton>
          </Grid>
        }
        <Grid>
          <Typography variant="h6">{`Cancelled Request #${signup.referenceNumber}`}</Typography>
        </Grid>
      </Grid>
      {isMobile &&
        <Divider sx={styles.divider} />
      }
      <Grid container>
        <Typography variant="body1" gutterBottom>{`${signup.minArrivalDate}-${signup.maxDepartureDate} Nights:${signup.nights}`}</Typography>
      </Grid>
      <Grid container>
        <Typography variant="body1" gutterBottom>{`Cancelled: ${dateStringToLocalDateFormatted(signup.cancelDate)}`}</Typography>
      </Grid>
      {signup.memberSignups.length > 0 &&
        <>
          <Typography variant="body1" sx={styles.sectionHeading}>Household Members</Typography>
          <DataGrid
            autoHeight
            columns={memberColumns()}
            columnVisibilityModel={{
              memberStatus: isMobile ? false : true
            }}
            density="compact"
            disableColumnSelector
            disableRowSelectionOnClick
            getRowId={(row) => row.memberSignupID} // Unique ID for each row
            hideFooter
            loading={isLoading}
            rows={signup.memberSignups}
            sx={styles.root}
          />
        </>
      }
      {signup.guestSignups.length > 0 &&
        <>
          <Typography variant="body1" sx={styles.sectionHeading}>Guests</Typography>
          <DataGrid
            autoHeight
            columns={guestColumns()}
            columnVisibilityModel={{
              guestType: isMobile ? false : true
            }}
            density="compact"
            disableColumnSelector
            disableRowSelectionOnClick
            getRowId={(row) => row.guestSignupID} // Unique ID for each row
            hideFooter
            loading={isLoading}
            rows={signup.guestSignups}
            sx={styles.root}
          />
        </>
      }
    </Box>
  );

}

export default CancelledSignup;